import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Header } from '@/components/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, BookOpen, Loader2, ArrowRight } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { Seo } from '@/components/Seo';

const ContributorsPage = () => {
  const navigate = useNavigate();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await apiFetch('/api/authors');
      if (cancelled) return;
      setAuthors(data?.authors || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background pb-16">
      <Seo
        path="/contributors"
        title="Contributors — ClearCommunicationApp"
        description="Meet the writers, advocates, and neurodivergent voices behind the ClearCommunicationApp blog. Real people building real tools for clearer communication."
      />
      {/* Schema.org CollectionPage of Person entities */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Contributors",
            url: "https://clearcommunicationapp.com/contributors",
            description:
              "Meet the writers and contributors behind the ClearCommunicationApp blog.",
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: authors.length,
              itemListElement: authors.map((a, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                item: {
                  "@type": "Person",
                  name: a.name,
                  url: `https://clearcommunicationapp.com/author/${a.slug}`,
                  image: a.picture || undefined,
                  description: a.bio || undefined,
                  jobTitle: a.is_admin ? "Founder & Author" : "Contributor",
                  worksFor: {
                    "@type": "Organization",
                    name: "ClearCommunicationApp",
                  },
                },
              })),
            },
          })}
        </script>
      </Helmet>
      <Header />

      <div className="container py-10 sm:py-14 max-w-5xl">
        {/* Hero */}
        <div className="mb-10 sm:mb-14 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#00E5CC]/15 mb-4">
            <Users className="h-6 w-6 text-[#00E5CC]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="contributors-page-heading">
            Meet our contributors
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
            The real people behind every article — neurodivergent voices, language coaches, parents, and advocates building tools for clearer communication.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#00E5CC]" />
          </div>
        ) : authors.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">No public contributor profiles yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="contributors-grid">
            {authors.map((a) => (
              <Card
                key={a.slug}
                className="cursor-pointer hover:border-[#00E5CC] hover:shadow-lg transition-all group"
                onClick={() => navigate(`/author/${a.slug}`)}
                data-testid={`contributor-card-${a.slug}`}
              >
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start gap-3 mb-3">
                    {a.picture ? (
                      <img
                        src={a.picture}
                        alt={a.name}
                        className="h-14 w-14 rounded-full border-2 border-[#00E5CC]/30 object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-14 w-14 rounded-full bg-[#00E5CC]/15 border-2 border-[#00E5CC]/30 flex items-center justify-center text-xl font-bold text-[#00E5CC] flex-shrink-0">
                        {a.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold text-base leading-tight truncate">{a.name}</h2>
                      <div className="flex items-center gap-1.5 mt-1">
                        {a.is_admin && (
                          <Badge variant="default" className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00E5CC] text-[10px] px-1.5 py-0">Founder</Badge>
                        )}
                        {!a.is_admin && a.is_contributor && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Contributor</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {a.bio ? (
                    <p className="text-sm text-muted-foreground line-clamp-3 mb-3">{a.bio}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground/70 italic mb-3">Bio coming soon.</p>
                  )}
                  <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3.5 w-3.5" />
                      {a.post_count} {a.post_count === 1 ? 'article' : 'articles'}
                    </span>
                    <span className="flex items-center gap-1 text-[#00E5CC] group-hover:gap-2 transition-all font-medium">
                      View
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributorsPage;
